import React from 'react';
import {OUTCOMES} from '../dice';

// Gouttes qui coulent depuis le haut : position (%), délai (ms), longueur de la traînée (vh)
const DRIPS = [
  {left: 6, delay: 0, length: 34},
  {left: 19, delay: 260, length: 22},
  {left: 33, delay: 120, length: 46},
  {left: 58, delay: 380, length: 28},
  {left: 71, delay: 60, length: 40},
  {left: 86, delay: 300, length: 30},
  {left: 95, delay: 180, length: 18},
];

// Griffures dans un repère 160 × 100 (recadré sur l'écran sans déformation) :
// début, fin, courbure, largeur max
const CLAWS = [
  {from: [52, 6], to: [90, 94], bend: -7, width: 5.5},
  {from: [66, 3], to: [104, 90], bend: -8, width: 7},
  {from: [80, 2], to: [116, 84], bend: -7, width: 5},
];

const STEPS = 28;

const point = ({from, to}, control, t) => [
  (1 - t) ** 2 * from[0] + 2 * (1 - t) * t * control[0] + t ** 2 * to[0],
  (1 - t) ** 2 * from[1] + 2 * (1 - t) * t * control[1] + t ** 2 * to[1],
];

/** Point de contrôle : milieu du segment décalé perpendiculairement de `bend`. */
const controlOf = ({from, to, bend}) => {
  const [dx, dy] = [to[0] - from[0], to[1] - from[1]];
  const length = Math.hypot(dx, dy);
  return [(from[0] + to[0]) / 2 - (dy / length) * bend, (from[1] + to[1]) / 2 + (dx / length) * bend];
};

// Irrégularité déterministe des bords : la chair se déchire, elle ne se coupe pas net
const tear = (t, seed) => 1 + 0.22 * Math.sin(t * 41 + seed) * Math.sin(t * 17 + seed * 2);

/** Entaille effilée aux deux bouts, la plus large au milieu (scale : part de la largeur). */
const gashPath = (claw, scale, seed = 0) => {
  const control = controlOf(claw);
  const left = [];
  const right = [];
  for (let i = 0; i <= STEPS; i += 1) {
    const t = i / STEPS;
    const [x, y] = point(claw, control, t);
    const [nx, ny] = point(claw, control, Math.min(1, t + 0.01));
    const [px, py] = point(claw, control, Math.max(0, t - 0.01));
    const [tx, ty] = [nx - px, ny - py];
    const norm = Math.hypot(tx, ty) || 1;
    const half = (claw.width * scale / 2) * Math.sin(Math.PI * t) ** 0.7;
    const [halfLeft, halfRight] = [half * tear(t, seed), half * tear(t, seed + 3)];
    left.push(`${(x - (ty / norm) * halfLeft).toFixed(2)} ${(y + (tx / norm) * halfLeft).toFixed(2)}`);
    right.push(`${(x + (ty / norm) * halfRight).toFixed(2)} ${(y - (tx / norm) * halfRight).toFixed(2)}`);
  }
  return `M${left.join(' L')} L${right.reverse().join(' L')} Z`;
};

const centerLine = (claw) => {
  const control = controlOf(claw);
  return `M${claw.from.join(' ')} Q${control.join(' ')} ${claw.to.join(' ')}`;
};

const GASHES = CLAWS.map((claw, index) => {
  const control = controlOf(claw);
  return {
    bleed: gashPath(claw, 1.9, index),
    rim: gashPath(claw, 1, index),
    wound: gashPath(claw, 0.45, index + 5),
    line: centerLine(claw),
    width: claw.width,
    // Gouttes qui perlent sous le bord inférieur de la plaie
    drops: [0.42, 0.63, 0.8].map(t => {
      const [x, y] = point(claw, control, t);
      return [x - claw.width * 0.35 * Math.sin(Math.PI * t), y + 0.6];
    }),
  };
});

/**
 * La Bête se manifeste : du sang coule (critique sanglant) ou des griffes lacèrent l'écran (échec bestial).
 * Rejoué à chaque changement de `key` ; purement décoratif.
 */
const VtmBeastMoment = ({outcome}) => (outcome === OUTCOMES.MESSY_CRITICAL ? (
  <div className="vtmBeast messy" aria-hidden="true">
    {DRIPS.map(drip => (
      <span
        key={drip.left}
        className="vtmDrip"
        style={{left: `${drip.left}%`, animationDelay: `${drip.delay}ms`, '--drip-length': `${drip.length}vh`}}
      />
    ))}
  </div>
) : (
  <div className="vtmBeast bestial" aria-hidden="true">
    <svg viewBox="0 0 160 100" preserveAspectRatio="xMidYMid slice">
      <defs>
        <filter id="vtmClawBlur" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="1.2" />
        </filter>
        {GASHES.map((gash, index) => (
          <mask key={gash.line} id={`vtmClawMask${index}`} maskUnits="userSpaceOnUse" x="0" y="0" width="160" height="100">
            <path
              className="vtmClawReveal"
              d={gash.line}
              pathLength="1"
              strokeWidth={gash.width * 2.5}
              style={{animationDelay: `${index * 70}ms`}}
            />
          </mask>
        ))}
      </defs>
      {GASHES.map((gash, index) => (
        <g key={gash.line} mask={`url(#vtmClawMask${index})`}>
          <path className="vtmClawBleed" d={gash.bleed} filter="url(#vtmClawBlur)" />
          <path className="vtmClawRim" d={gash.rim} />
          <path className="vtmClawWound" d={gash.wound} />
        </g>
      ))}
      {GASHES.flatMap((gash, index) => gash.drops.map(([x, y], dropIndex) => (
        <path
          key={`${index}-${dropIndex}`}
          className="vtmClawDrop"
          d={`M${x} ${y} C ${x + 0.5} ${y + 1.2}, ${x + 0.75} ${y + 1.8}, ${x} ${y + 2.3} C ${x - 0.75} ${y + 1.8}, ${x - 0.5} ${y + 1.2}, ${x} ${y} Z`}
          style={{animationDelay: `${300 + index * 90 + dropIndex * 140}ms`}}
        />
      )))}
    </svg>
  </div>
));

export default VtmBeastMoment;
